/**
 * Sync Engine for pyxl Desktop
 *
 * Handles syncing locally-queued actions to Firestore when
 * connectivity is restored. Only active in Tauri (desktop).
 *
 * Architecture:
 * ─────────────
 *                     ┌────────────┐
 *  App goes online ──▶│ processSyncQueue │──▶ For each queued action:
 *                     └──────┬─────┘         1. Execute against Firestore
 *                            │               2. Mark run as synced
 *                            │               3. Remove from queue
 *                            │
 *                            ▼
 *                     ┌────────────┐
 *                     │ SyncStatus │──▶ UI shows progress
 *                     └────────────┘
 *
 * Conflict Resolution (last-write-wins):
 * ──────────────────────────────────────
 * - Runs: Firestore uses addDoc, so duplicates are impossible.
 *   Each run has a unique local ID. If the same run was already
 *   synced (edge case: re-queue after partial failure), the
 *   hash field allows detection.
 *
 * - Stats: updateUserStats uses server-side increments, so
 *   applying the same delta twice would over-count. We guard
 *   against this by only syncing from the sync queue and
 *   removing the action after success.
 *
 * - Leaderboard: submitToLeaderboard only updates if the
 *   new score is higher (Firestore-side check), so re-submits
 *   are idempotent.
 *
 * Error handling:
 * ──────────────
 * - Each action has a retry_count. After 5 failures, the action
 *   is kept in the queue but skipped during processing.
 * - The last_error field records what went wrong.
 */

import { isTauri } from './database';
import { isOnline, onConnectivityChange } from './dataLayer';

// ─── Sync Status (observable from UI) ──────────────────────

export type SyncState = 'idle' | 'syncing' | 'error' | 'offline';

interface SyncStatus {
  state: SyncState;
  pending: number;
  lastSyncedAt: number | null;
  lastError: string | null;
}

let _status: SyncStatus = {
  state: 'idle',
  pending: 0,
  lastSyncedAt: null,
  lastError: null,
};

const _statusListeners = new Set<(status: SyncStatus) => void>();

function setStatus(update: Partial<SyncStatus>): void {
  _status = { ..._status, ...update };
  _statusListeners.forEach((fn) => fn(_status));
}

/**
 * Get current sync status.
 */
export function getSyncStatus(): SyncStatus {
  return _status;
}

/**
 * Subscribe to sync status changes. Returns unsubscribe function.
 */
export function onSyncStatusChange(
  listener: (status: SyncStatus) => void
): () => void {
  _statusListeners.add(listener);
  return () => _statusListeners.delete(listener);
}

// ─── Sync Engine ──────────────────────────────────────────

const MAX_RETRIES = 5;
let _syncInProgress = false;
let _unsubConnectivity: (() => void) | null = null;

/**
 * Initialize the sync engine.
 * Listens for connectivity changes and processes the queue
 * when coming back online. Also processes on startup if online.
 *
 * Only active in Tauri. Safe to call in web (no-op).
 */
export function initSyncEngine(): void {
  if (!isTauri()) return;

  // Set initial state
  setStatus({ state: isOnline() ? 'idle' : 'offline' });

  // Listen for connectivity changes
  _unsubConnectivity = onConnectivityChange((online) => {
    if (online) {
      setStatus({ state: 'idle' });
      // Process queue after a short debounce
      // (connectivity can flap briefly)
      setTimeout(() => {
        if (isOnline()) processSyncQueue();
      }, 2000);
    } else {
      setStatus({ state: 'offline' });
    }
  });

  // Process existing queue on startup (if online)
  if (isOnline()) {
    setTimeout(() => processSyncQueue(), 1000);
  }
}

/**
 * Stop the sync engine and clean up listeners.
 */
export function stopSyncEngine(): void {
  _unsubConnectivity?.();
  _unsubConnectivity = null;
}

/**
 * Process all pending sync actions.
 * Runs sequentially to avoid race conditions.
 */
export async function processSyncQueue(): Promise<void> {
  if (_syncInProgress) return;
  if (!isOnline()) return;

  _syncInProgress = true;

  try {
    const { getPendingSyncActions, removeSyncAction, recordSyncError, markRunSynced } =
      await import('./database');

    const actions = await getPendingSyncActions();
    if (actions.length === 0) {
      setStatus({ state: 'idle', pending: 0 });
      return;
    }

    setStatus({ state: 'syncing', pending: actions.length, lastError: null });

    let processed = 0;
    let errors = 0;

    for (const action of actions) {
      // Skip actions that have failed too many times
      if (action.retry_count >= MAX_RETRIES) {
        continue;
      }

      // Bail if we've gone offline mid-sync
      if (!isOnline()) {
        setStatus({ state: 'offline', pending: actions.length - processed });
        return;
      }

      try {
        const payload = JSON.parse(action.payload);

        switch (action.action) {
          case 'save_run': {
            const { saveRun } = await import('../firestore');
            await saveRun(payload);
            // Mark the local run as synced (if it has a localId)
            if (payload.localId) {
              await markRunSynced(payload.localId);
            }
            break;
          }

          case 'update_stats': {
            const { updateUserStats } = await import('../firestore');
            await updateUserStats(payload.uid, payload.runScore, payload.perfectCount);
            break;
          }

          case 'submit_leaderboard': {
            const { submitToLeaderboard } = await import('../firestore');
            await submitToLeaderboard(payload);
            break;
          }

          default:
            console.warn(`[sync] Unknown action: ${action.action}`);
        }

        // Success — remove from queue
        await removeSyncAction(action.id);
        processed++;
        setStatus({ pending: actions.length - processed - errors });
      } catch (err) {
        errors++;
        const message = err instanceof Error ? err.message : String(err);
        console.warn(`[sync] Failed to process action ${action.id}:`, message);
        await recordSyncError(action.id, message);
        setStatus({ lastError: message });
      }
    }

    const finalState = errors > 0 ? 'error' : 'idle';
    setStatus({
      state: finalState,
      pending: actions.length - processed,
      lastSyncedAt: processed > 0 ? Date.now() : _status.lastSyncedAt,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[sync] Queue processing failed:', message);
    setStatus({ state: 'error', lastError: message });
  } finally {
    _syncInProgress = false;
  }
}
